import firebase from "firebase/compat/app";
import "firebase/compat/functions";

const META_PROMPT_ID = "asi-prompt-refinement-meta";

export function createConversationSessionId() {
  return firebase.firestore().collection("conversationSessions").doc().id;
}

/**
 * Append a turn to a conversation session (server-side write via Cloud Function).
 */
export async function appendConversationTurn({
  sessionId,
  organizationId,
  productId = "unknown",
  conversationTarget = "unknown",
  promptIds = [],
  compositionModeActive = null,
  tciId = null,
  institutionId: institutionIdOverride = null,
  allowAnonymousPublic = false,
  visitStartSessionId = null,
  turn,
}) {
  if (!sessionId || !organizationId || !turn?.text) return;

  const callable = firebase
    .app()
    .functions("us-central1")
    .httpsCallable("writeConversationTurn");

  await callable({
    sessionId,
    organizationId,
    institutionId: institutionIdOverride || organizationId,
    productId,
    conversationTarget,
    promptIds,
    compositionModeActive,
    tciId,
    allowAnonymousPublic: allowAnonymousPublic === true,
    ...(visitStartSessionId
      ? { visitStartSessionId: String(visitStartSessionId).trim() }
      : {}),
    turn: {
      turnIndex: turn.turnIndex ?? 0,
      speaker: turn.speaker === "user" ? "user" : "system",
      text: String(turn.text).trim(),
    },
  });
}

/**
 * Parse a plain-text transcript with "User:" / "Assistant:" (or "System:") lines into turns.
 */
export function parseTranscriptLines(transcript) {
  const lines = String(transcript || "").split(/\r?\n/);
  const turns = [];
  let currentSpeaker = null;
  let buffer = [];

  const flush = () => {
    const text = buffer.join("\n").trim();
    if (currentSpeaker && text) {
      turns.push({ speaker: currentSpeaker, text, turnIndex: turns.length });
    }
    buffer = [];
  };

  for (const line of lines) {
    const userMatch = /^user\s*:/i.test(line);
    const assistantMatch = /^(assistant|system|ai)\s*:/i.test(line);
    if (userMatch) {
      flush();
      currentSpeaker = "user";
      buffer.push(line.replace(/^user\s*:/i, "").trim());
    } else if (assistantMatch) {
      flush();
      currentSpeaker = "system";
      buffer.push(line.replace(/^(assistant|system|ai)\s*:/i, "").trim());
    } else if (currentSpeaker) {
      buffer.push(line);
    }
  }
  flush();
  return turns;
}

/**
 * Persist a completed transcript as conversation session turns (batch append).
 */
export async function persistTranscriptSession({
  sessionId,
  organizationId,
  productId,
  conversationTarget,
  promptIds,
  transcript,
  tciId = null,
  institutionId = null,
  allowAnonymousPublic = false,
}) {
  const turns = parseTranscriptLines(transcript);
  for (const turn of turns) {
    await appendConversationTurn({
      sessionId,
      organizationId,
      productId,
      conversationTarget,
      promptIds,
      tciId,
      institutionId: institutionId || organizationId,
      allowAnonymousPublic,
      turn,
    });
  }
}

export { META_PROMPT_ID };
